import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import store from '@state/store'
import { MINUTES_TIMEOUT_REQUESTS } from '@constants/Numbers'
import { carteiraGrupoFamiliar } from '@modules/consolidator/constants/URL'
import {
  diffTimeMinutes,
  formatQueryParams,
  safeJsonParse,
} from '@utils/Format'
import { isSameScope } from '@modules/consolidator/utils/Api/Api'
import { adaptGroupWalletAnalysisResponse } from '@modules/consolidator/utils/adapt-group-wallet-analysis-response/adapt-group-wallet-analysis-response'
import { adaptStatisticsPerAssetResponse } from '@modules/consolidator/utils/adapt-statistics-per-asset-response/adapt-statistics-per-asset-response'
import { buildGroupWalletOptionsFields } from '@modules/consolidator/utils/build-group-wallet-options-fields/build-group-wallet-options-fields'
import { buildWalletAnalysisRequest } from '@modules/consolidator/utils/build-wallet-analysis-request/build-wallet-analysis-request'
import {
  buildParamsKey,
  serializeGroupWalletParams,
} from '@modules/consolidator/utils/serialize-group-wallet-params/serialize-group-wallet-params'

const GROUP_BY_MEMBER = 'id_cliente'

const GROUP_BY_ASSET = 'id_dado_ativo'

const DATA_REQUESTED_SO_STATISTICS = ['statistics']

// `PL` sozinho traz a entrada do breakdown SEM o objeto `statistics` — e e dele
// que saem TODAS as colunas de metrica de "Por Carteira" (`inicio`, `12M`,
// `inicio_relativo`, `vol`, `sharp_ratio`, `data_inicio`). Medido no endpoint em
// 2026-08-07: com `['PL']` a entrada tem so `pl_fim/pl_alocacao/.../PL` e a
// tabela inteira renderiza "-" fora da linha de Total.
//
// `statistics` NAO engorda a resposta: o envelope volta com os mesmos 17 blocos
// nos dois casos — `data_requested` decide o que e CALCULADO dentro de cada
// entrada do breakdown, nao o que trafega.
const DATA_REQUESTED_SO_BREAKDOWN = ['PL', 'statistics']

/**
 * Consolidação de carteira por GRUPO FAMILIAR.
 *
 * Classe irmã de `apiConsolidatorWalletAnalysis` (por cliente). São duas e não
 * uma porque a guarda de identificação é diferente: lá `id_cliente_app` é
 * obrigatório e essa exigência protege o caminho por cliente; aqui o
 * obrigatório é `id_grupo_familiar`.
 *
 * ★ Nenhuma estatística é calculada nem agregada aqui. Rentabilidade,
 * volatilidade e Sharpe do grupo vêm prontos do backend, calculados sobre a
 * união dos ativos dos membros. Agregar métricas por membro no cliente ignora a
 * covariância entre as carteiras e produz número errado.
 */
class APIConsolidatorGroupWalletAnalysis extends APITemplate {
  constructor() {
    super()

    this.reqCarregar = null
    this.loadedGroupId = null
    this.loadedParamsKey = null
    this.reqMembros = null
    this.reqMembrosParamsKey = null
    this.seqMembros = 0
    this.loadedMemberGroupId = null
    this.loadedMemberParamsKey = null
    this.reqAtivos = null
    this.reqAtivosParamsKey = null
    this.seqAtivos = 0
    this.loadedAssetGroupId = null
    this.loadedAssetParamsKey = null
  }

  /**
   * @param {Object} args
   * @param {String} args.id_grupo_familiar UUID do grupo (obrigatório).
   * @param {Array}  [args.data_requested] Blocos pedidos ao backend.
   * @param {Boolean} [args.forceUpdate] Ignora o cache do store.
   * @param {Object} [args.filters] Demais filtros, já com nome de param da API.
   * @returns {Promise<Object>} Resposta adaptada (nunca lança).
   */
  async carregar({
    id_grupo_familiar,
    data_requested,
    investment_type,
    forceUpdate,
    ...filters
  } = {}) {
    const { error, params } = serializeGroupWalletParams({
      id_grupo_familiar,
      investment_type,
      data_requested: this.resolveDataRequested({
        data_requested,
        investment_type,
      }),
      ...filters,
    })

    if (error) {
      return adaptGroupWalletAnalysisResponse({
        status: 400,
        value: { status: 400, message: error },
      })
    }

    const paramsKey = buildParamsKey(params)
    const cached = this.getCachedResponse({
      forceUpdate,
      scopeKey: id_grupo_familiar,
      paramsKey,
    })
    if (cached) return cached

    if (!isSameScope(this.loadedGroupId, id_grupo_familiar)) {
      store.dispatch('consolidator/setFamilyGroupWallet', null)
    }

    const analysis = await this._fetchAndDispatch({
      id_grupo_familiar,
      params,
      paramsKey,
    })

    return analysis
  }

  /**
   * Breakdown por MEMBRO, buscado à parte do payload principal.
   *
   * A seção "Por Carteira" é fixa na tela e sempre mostra uma linha por membro,
   * mas `carregar` devolve o breakdown na dimensão que o usuário escolheu em
   * "Agrupar por" — com qualquer valor que não seja o membro, a seção ficava
   * vazia mandando ele trocar o filtro pra ver a própria tabela. Esta carga
   * fixa `group_by=id_cliente` e vive num slot próprio do store, então os dois
   * recortes coexistem: o donut e os cards seguem o filtro, a tabela dos
   * membros não.
   *
   * Recebe os MESMOS filtros da carga principal (conta, membro, período, tipo
   * de investimento): o que não pode variar é a dimensão do agrupamento, não o
   * recorte — as duas seções precisam falar do mesmo universo de ativos.
   *
   * @param {Object} args Iguais aos de `carregar`, menos `group_by`.
   * @returns {Promise<Object>} Resposta adaptada (nunca lança).
   */
  carregarBreakdownPorMembro({
    id_grupo_familiar,
    data_requested,
    investment_type,
    forceUpdate,
    // eslint-disable-next-line no-unused-vars
    group_by,
    ...filters
  } = {}) {
    const { error, params } = serializeGroupWalletParams({
      id_grupo_familiar,
      investment_type,
      // So o breakdown: esta carga existe pra alimentar "Por Carteira" e nada
      // mais. Herdando o `data_requested` padrao ela pedia os 9 blocos (PL,
      // rentabilidade, statistics, posicao_completa, ...) pra consumir um —
      // dobrando o custo do endpoint mais pesado da tela a cada filtro.
      data_requested: this.resolveDataRequested({
        data_requested: data_requested || DATA_REQUESTED_SO_BREAKDOWN,
        investment_type,
      }),
      group_by: GROUP_BY_MEMBER,
      ...filters,
    })

    if (error) {
      return adaptGroupWalletAnalysisResponse({
        status: 400,
        value: { status: 400, message: error },
      })
    }

    const paramsKey = buildParamsKey(params)
    // Reusar a promessa em voo so vale quando ela e do MESMO recorte. Sem
    // comparar o `paramsKey`, uma chamada com filtro novo recebia a resposta do
    // filtro anterior — e o `loadedMemberParamsKey` acabava gravado com a chave
    // errada, deixando "Por Carteira" no recorte velho ate a proxima mudanca.
    if (this.reqMembros && isSameScope(this.reqMembrosParamsKey, paramsKey)) {
      return this.reqMembros
    }
    if (this.isMemberBreakdownCached({ id_grupo_familiar, paramsKey, forceUpdate })) {
      return null
    }
    if (!isSameScope(this.loadedMemberGroupId, id_grupo_familiar)) {
      store.dispatch('consolidator/setFamilyGroupMemberBreakdown', null)
    }
    return this._fetchMemberBreakdown({ id_grupo_familiar, params, paramsKey })
  }

  carregarEstatisticasPorAtivo({
    id_grupo_familiar,
    investment_type,
    forceUpdate,
    // eslint-disable-next-line no-unused-vars
    group_by,
    // eslint-disable-next-line no-unused-vars
    data_requested,
    ...filters
  } = {}) {
    const { error, params } = serializeGroupWalletParams({
      id_grupo_familiar,
      investment_type,
      data_requested: this.resolveDataRequested({
        data_requested: DATA_REQUESTED_SO_STATISTICS,
        investment_type,
      }),
      group_by: GROUP_BY_ASSET,
      ...filters,
    })

    if (error) {
      return adaptGroupWalletAnalysisResponse({
        status: 400,
        value: { status: 400, message: error },
      })
    }

    const paramsKey = buildParamsKey(params)
    if (this.reqAtivos && isSameScope(this.reqAtivosParamsKey, paramsKey)) {
      return this.reqAtivos
    }
    if (this.isAssetStatisticsCached({ id_grupo_familiar, paramsKey, forceUpdate })) {
      return null
    }
    if (!isSameScope(this.loadedAssetGroupId, id_grupo_familiar)) {
      store.dispatch('consolidator/setFamilyGroupAssetStatistics', null)
    }
    return this._fetchAssetStatistics({ id_grupo_familiar, params, paramsKey })
  }

  isAssetStatisticsCached({ id_grupo_familiar, paramsKey, forceUpdate } = {}) {
    if (forceUpdate) return false
    if (!isSameScope(this.loadedAssetGroupId, id_grupo_familiar)) return false
    if (!isSameScope(this.loadedAssetParamsKey, paramsKey)) return false
    return !diffTimeMinutes(
      store.getters['consolidator/familyGroupAssetStatisticsLastUpdate'],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  async _fetchAssetStatistics({ id_grupo_familiar, params, paramsKey }) {
    const token = (this.seqAtivos = (this.seqAtivos || 0) + 1)
    this.reqAtivosParamsKey = paramsKey
    store.dispatch('consolidator/setFamilyGroupAssetStatisticsLoading', true)
    const url =
      carteiraGrupoFamiliar.consolidation({ id: id_grupo_familiar }) +
      formatQueryParams(params)

    this.reqAtivos = con
      .get(url)
      .then(safeJsonParse)
      .then(adaptStatisticsPerAssetResponse)
    const adapted = await this.reqAtivos

    if (token === this.seqAtivos) {
      this._publicarEstatisticasPorAtivo({ adapted, id_grupo_familiar, paramsKey })
    }

    return adapted
  }

  _publicarEstatisticasPorAtivo({ adapted, id_grupo_familiar, paramsKey }) {
    const payload =
      adapted && adapted.ok ? adapted.statistics_by_asset || {} : {}
    store.dispatch('consolidator/setFamilyGroupAssetStatistics', payload)
    if (adapted && adapted.ok) {
      this.loadedAssetGroupId = id_grupo_familiar
      this.loadedAssetParamsKey = paramsKey
    }
    store.dispatch('consolidator/setFamilyGroupAssetStatisticsLoading', false)
    this.reqAtivos = null
    this.reqAtivosParamsKey = null
  }

  isMemberBreakdownCached({ id_grupo_familiar, paramsKey, forceUpdate } = {}) {
    if (forceUpdate) return false
    if (!isSameScope(this.loadedMemberGroupId, id_grupo_familiar)) return false
    if (!isSameScope(this.loadedMemberParamsKey, paramsKey)) return false
    return !diffTimeMinutes(
      store.getters['consolidator/familyGroupMemberBreakdownLastUpdate'],
      MINUTES_TIMEOUT_REQUESTS
    )
  }

  async _fetchMemberBreakdown({ id_grupo_familiar, params, paramsKey }) {
    // Token de sequencia: o dedupe por `paramsKey` impede REUSAR a promessa
    // errada, mas nao ordena a CHEGADA. Sem isto, a resposta do filtro anterior
    // que resolve por ultimo sobrescreve a do filtro atual no store — e ainda
    // grava a chave de cache errada.
    const token = (this.seqMembros = (this.seqMembros || 0) + 1)
    this.reqMembrosParamsKey = paramsKey
    store.dispatch('consolidator/setFamilyGroupMemberBreakdownLoading', true)
    const url =
      carteiraGrupoFamiliar.consolidation({ id: id_grupo_familiar }) +
      formatQueryParams(params)

    this.reqMembros = con
      .get(url)
      .then(safeJsonParse)
      .then(adaptGroupWalletAnalysisResponse)
    const adapted = await this.reqMembros

    if (token === this.seqMembros) {
      this._publicarBreakdownPorMembro({ adapted, id_grupo_familiar, paramsKey })
    }

    return adapted
  }

  _publicarBreakdownPorMembro({ adapted, id_grupo_familiar, paramsKey }) {
    store.dispatch('consolidator/setFamilyGroupMemberBreakdown', adapted || null)
    if (adapted && adapted.ok) {
      this.loadedMemberGroupId = id_grupo_familiar
      this.loadedMemberParamsKey = paramsKey
    }
    store.dispatch('consolidator/setFamilyGroupMemberBreakdownLoading', false)
    this.reqMembros = null
    this.reqMembrosParamsKey = null
  }

  async _fetchAndDispatch({ id_grupo_familiar, params, paramsKey }) {
    store.dispatch('consolidator/setFamilyGroupLoading', true)

    const url =
      carteiraGrupoFamiliar.consolidation({ id: id_grupo_familiar }) +
      formatQueryParams(params)

    this.reqCarregar = con
      .get(url)
      .then(safeJsonParse)
      .then(adaptGroupWalletAnalysisResponse)
    const adapted = await this.reqCarregar

    this.dispatchAdapted(adapted)
    if (adapted && adapted.ok) {
      this.loadedGroupId = id_grupo_familiar
      this.loadedParamsKey = paramsKey
    }
    store.dispatch('consolidator/setFamilyGroupLoading', false)
    this.reqCarregar = null

    return adapted
  }

  /**
   * Só há cache quando a última carga foi bem-sucedida para o MESMO grupo E o
   * MESMO recorte de filtros — ambos só são gravados no sucesso, então erro
   * nunca é servido de cache.
   *
   * ★ Os filtros fazem parte da identidade do cache porque o store guarda UM
   * payload — o da última carga. Servir esse payload quando o recorte mudou
   * devolve os números do recorte anterior: foi o que fez o filtro de membro
   * não surtir efeito nenhum na tela (a requisição sequer era disparada).
   */
  getCachedResponse({ forceUpdate, scopeKey, paramsKey } = {}) {
    if (this.reqCarregar) return this.reqCarregar
    if (!isSameScope(this.loadedGroupId, scopeKey)) return null
    if (!isSameScope(this.loadedParamsKey, paramsKey)) return null

    const cached = store.getters['consolidator/familyGroupWalletData']

    if (store.getters['consolidator/familyGroupLoading']) return cached
    if (forceUpdate) return null

    const isCacheValid = !diffTimeMinutes(
      store.getters['consolidator/familyGroupWalletLastUpdate'],
      MINUTES_TIMEOUT_REQUESTS
    )
    if (isCacheValid && cached) return cached

    return null
  }

  /**
   * A resposta é persistida mesmo em erro: os estados de bloqueio (403), de
   * grupo sem membros (404) e de limite de ativos (400) são renderizados a
   * partir do par (status, shape do payload) guardado no store.
   */
  dispatchAdapted(adapted) {
    store.dispatch('consolidator/setFamilyGroupWallet', adapted || null)
  }

  /**
   * Lista de blocos pedidos ao backend, sem os que a rota de grupo recusa.
   *
   * Quando o chamador não passa nada, a lista padrão vem de
   * `buildWalletAnalysisRequest` — a mesma fonte do caminho por cliente, para
   * que as duas telas não divirjam com o tempo.
   */
  resolveDataRequested({ data_requested, investment_type } = {}) {
    const hasExplicitList =
      Array.isArray(data_requested) && data_requested.length > 0
    return hasExplicitList
      ? data_requested
      : this.buildDefaultDataRequested(investment_type)
  }

  buildDefaultDataRequested(investmentType) {
    const params = buildWalletAnalysisRequest({
      investmentTypeFilter: investmentType,
    })
    return Array.isArray(params.data_requested) ? params.data_requested : []
  }

  /**
   * Valores disponíveis de cada filtro, via OPTIONS da própria rota. É o que
   * alimenta o filtro "Conta" da barra de filtros do grupo — sem esta chamada,
   * o seletor não tem de onde tirar os valores.
   *
   * @param {Object} args
   * @param {String} args.id_grupo_familiar UUID do grupo.
   * @returns {Promise<Array>} Campos de filtro.
   */
  async loadOptions({ id_grupo_familiar } = {}) {
    if (!id_grupo_familiar) return []

    store.dispatch('consolidator/setFamilyGroupOptionsFieldsLoading', true)
    try {
      const url = carteiraGrupoFamiliar.consolidation({
        id: id_grupo_familiar,
      })
      const r = await con.options(url)
      const groupData = r && r.group_data ? r.group_data : null
      const fields = buildGroupWalletOptionsFields(groupData)
      store.dispatch('consolidator/setFamilyGroupOptionsFields', fields)
      return fields
    } finally {
      store.dispatch('consolidator/setFamilyGroupOptionsFieldsLoading', false)
    }
  }
}

const apiConsolidatorGroupWalletAnalysis =
  new APIConsolidatorGroupWalletAnalysis()
export { apiConsolidatorGroupWalletAnalysis }
